import { c as _c } from "react/compiler-runtime";
import { Box, Stack } from "@mui-wrapper";
import { useEffect, useMemo, useRef, useState } from "react";
import { DateTimeUtils } from "@stellar/utils";
import { analyticsApiClient } from "@api/analytics-api-client";
import { AnalyticsBarChart } from "@components/analytics/in-house/analytics-bar-chart";
import { AnalyticsLineChart } from "@components/analytics/in-house/analytics-line-chart";
import { CommonStyles } from "@styles/common-styles";
import { Text, ToggleGroup } from "@flat-ui";
import { ANALYTICS_CHART_TOGGLE_ITEMS, METRIC_CHART_METADATA } from "@components/analytics/in-house/constants/analytics-constants";
import { useCompanyId } from "@context-providers/company-id-provider";
import { buildDailyMetricDataset, formatDateOnly } from "@components/analytics/in-house/utils/analytics-utils";
import { DataViewEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
/** Approximate width needed by one date tick label (e.g. "Jun 30"). */
const X_TICK_LABEL_MIN_WIDTH = 64;
/** Conservative fallback label count before first resize measurement arrives. */
const X_TICK_FALLBACK_VISIBLE_LABELS = 7;

/**
 * Renders the in-house analytics chart area using the selected range and metric from parent state.
 * The chart data is derived from mock metrics and filtered by the selected day range.
 */
export function AnalyticsChart(t0) {
  const $ = _c(38);
  const {
    selectedRange,
    metricKey
  } = t0;
  const {
    trackEvent
  } = useTrackEvent();
  const companyId = useCompanyId();
  const chartContainerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [chartType, setChartType] = useState("bar");
  const [projectMetrics, setProjectMetrics] = useState(null);
  const {
    chartTitle,
    yAxisLabel,
    seriesUnitLabel
  } = METRIC_CHART_METADATA[metricKey];
  let t1;
  if ($[0] !== chartType || $[1] !== trackEvent) {
    t1 = function handleChartTypeChange(nextChartType) {
      if (nextChartType === chartType) {
        return;
      }
      setChartType(nextChartType);
      trackEvent({
        name: DataViewEvents.changeViewMode,
        props: {
          viewType: nextChartType
        }
      });
    };
    $[0] = chartType;
    $[1] = trackEvent;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleChartTypeChange = t1;
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const element = chartContainerRef.current;
      if (!element) {
        return;
      }
      const observer = new ResizeObserver(entries => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        setChartWidth(entry.contentRect.width);
      });
      observer.observe(element);
      return () => {
        observer.disconnect();
      };
    };
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== companyId || $[6] !== selectedRange) {
    t4 = () => {
      const [startDate, endDate] = selectedRange;
      if (!startDate || !endDate) {
        setProjectMetrics(null);
        return;
      }
      const fetchProjectMetrics = async function fetchProjectMetrics(from, to) {
        const response = await analyticsApiClient.analytics.getProjectMetrics({
          workspaceId: companyId,
          shouldUseMock: true,
          query: {
            from: formatDateOnly(from),
            to: formatDateOnly(to)
          }
        });
        setProjectMetrics(response);
      };
      fetchProjectMetrics(startDate, endDate);
    };
    t5 = [companyId, selectedRange];
    $[5] = companyId;
    $[6] = selectedRange;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  const [startDate_0, endDate_0] = selectedRange;
  let t6;
  if ($[9] !== endDate_0 || $[10] !== startDate_0) {
    t6 = startDate_0 && endDate_0 ? Math.abs(DateTimeUtils.diffBetweenTwoDatesInDays(endDate_0, startDate_0)) + 1 : 0;
    $[9] = endDate_0;
    $[10] = startDate_0;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const totalDays = t6;
  let t7;
  if ($[12] !== metricKey || $[13] !== projectMetrics || $[14] !== startDate_0 || $[15] !== totalDays) {
    t7 = buildDailyMetricDataset({
      projectMetrics,
      startDate: startDate_0 ?? new Date(),
      totalDays,
      metricKey
    });
    $[12] = metricKey;
    $[13] = projectMetrics;
    $[14] = startDate_0;
    $[15] = totalDays;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const dataset = t7;
  let t8;
  bb0: {
    if (dataset.length <= 1) {
      t8 = 1;
      break bb0;
    }
    const maxLabelCount = chartWidth > 0 ? Math.max(1, Math.floor(chartWidth / X_TICK_LABEL_MIN_WIDTH)) : X_TICK_FALLBACK_VISIBLE_LABELS;
    t8 = Math.max(1, Math.ceil(dataset.length / maxLabelCount));
  }
  const xTickStep = t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      border: CommonStyles.Borders.divider,
      mt: 1
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      xs: "column",
      sm: "row"
    };
    t11 = {
      xs: "flex-start",
      sm: "center"
    };
    $[18] = t10;
    $[19] = t11;
  } else {
    t10 = $[18];
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      alignItems: t11,
      justifyContent: "space-between",
      px: {
        xs: 2,
        sm: 4
      },
      pt: {
        xs: 2,
        sm: 4
      },
      gap: {
        xs: 1.5,
        sm: 0
      }
    };
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== chartTitle) {
    t13 = <Text variant="heading20">{chartTitle}</Text>;
    $[21] = chartTitle;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== chartType || $[24] !== handleChartTypeChange) {
    t14 = <ToggleGroup items={ANALYTICS_CHART_TOGGLE_ITEMS} value={chartType} onChange={handleChartTypeChange} />;
    $[23] = chartType;
    $[24] = handleChartTypeChange;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t13 || $[27] !== t14) {
    t15 = <Stack direction={t10} sx={t12}>{t13}{t14}</Stack>;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== chartType || $[30] !== dataset || $[31] !== seriesUnitLabel || $[32] !== xTickStep || $[33] !== yAxisLabel) {
    t16 = chartType === "bar" ? <AnalyticsBarChart dataset={dataset} xTickStep={xTickStep} yAxisLabel={yAxisLabel} seriesUnitLabel={seriesUnitLabel} /> : <AnalyticsLineChart dataset={dataset} xTickStep={xTickStep} yAxisLabel={yAxisLabel} seriesUnitLabel={seriesUnitLabel} />;
    $[29] = chartType;
    $[30] = dataset;
    $[31] = seriesUnitLabel;
    $[32] = xTickStep;
    $[33] = yAxisLabel;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = <Box ref={chartContainerRef} sx={t9}>{t15}{t16}</Box>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  return t17;
}